import { useEffect, useState } from "react";
import { LogOut, X, ChevronRight, ChevronDown } from "lucide-react";
import { cn, toTitleCase } from "../lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { BrandLogo } from "./BrandLogo";

function pathActive(pathname, href) {
  if (!href) return false;
  if (pathname === href) return true;
  const roots = ["/admin", "/ftm", "/church", "/set-member", "/set-leader", "/profile"];
  if (roots.includes(href)) return pathname === href;
  return href !== "/" && pathname.startsWith(href);
}

function NavLinkItem({ item, onLinkClick, nested = false }) {
  const location = useLocation();
  const active = pathActive(location.pathname, item.href);
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      onClick={onLinkClick}
      className={cn(
        "flex items-center gap-3 rounded-lg transition-all duration-200 group",
        nested ? "px-3 py-2.5 ml-2" : "px-4 py-3",
        active
          ? "bg-gradient-to-r from-[#1E74FD] to-blue-600 text-white shadow-md shadow-[#1E74FD]/30"
          : "text-foreground hover:bg-[#1E74FD]/10 hover:text-[#1E74FD]"
      )}
    >
      {Icon ? (
        <Icon
          className={cn(
            "transition-all duration-200 shrink-0",
            nested ? "h-4 w-4" : "h-5 w-5",
            active ? "text-white" : "text-muted-foreground group-hover:text-[#1E74FD]"
          )}
        />
      ) : null}
      <span
        className={cn(
          "font-medium transition-colors duration-200 min-w-0 truncate",
          nested ? "text-xs" : "text-sm",
          active ? "text-white font-semibold" : "text-foreground"
        )}
      >
        {item.label}
      </span>
      {active && !nested && <ChevronRight className="h-4 w-4 ml-auto text-white shrink-0" />}
    </Link>
  );
}

function NavGroup({ item, onLinkClick }) {
  const location = useLocation();
  const children = item.children || [];
  const childActive = children.some((child) => pathActive(location.pathname, child.href));
  const [open, setOpen] = useState(childActive);
  const Icon = item.icon;

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group text-left",
          childActive
            ? "bg-[#1E74FD]/10 text-[#1E74FD]"
            : "text-foreground hover:bg-[#1E74FD]/10 hover:text-[#1E74FD]"
        )}
      >
        {Icon ? (
          <Icon
            className={cn(
              "h-5 w-5 shrink-0",
              childActive
                ? "text-[#1E74FD]"
                : "text-muted-foreground group-hover:text-[#1E74FD]"
            )}
          />
        ) : null}
        <span
          className={cn(
            "font-medium text-sm flex-1 min-w-0 truncate",
            childActive ? "text-[#1E74FD] font-semibold" : "text-foreground"
          )}
        >
          {item.label}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 transition-transform duration-200",
            open ? "rotate-0" : "-rotate-90",
            childActive ? "text-[#1E74FD]" : "text-muted-foreground"
          )}
        />
      </button>
      {open ? (
        <div className="space-y-0.5 border-l border-border/70 ml-5 pl-1">
          {children.map((child) => (
            <NavLinkItem
              key={child.href}
              item={child}
              onLinkClick={onLinkClick}
              nested
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Sidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { user, logout, menuItems } = useAuth();

  const getUserInitials = () => {
    const name = user?.full_name || user?.name;
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return "U";
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const renderNav = (onLinkClick) =>
    menuItems.map((item) => {
      if (Array.isArray(item.children) && item.children.length) {
        return (
          <NavGroup
            key={item.label}
            item={item}
            onLinkClick={onLinkClick}
          />
        );
      }
      return (
        <NavLinkItem
          key={item.href || item.label}
          item={item}
          onLinkClick={onLinkClick}
        />
      );
    });

  return (
    <>
      <aside className="hidden lg:flex fixed left-0 top-[calc(4rem+env(safe-area-inset-top))] h-[calc(100svh-4rem-env(safe-area-inset-top))] w-64 flex-col border-r border-border z-40 shadow-lg bg-background/95 backdrop-blur-md">
        <nav className="flex-1 space-y-1 p-4 overflow-y-auto overscroll-contain">{renderNav()}</nav>
        <div className="p-4 border-t border-border space-y-3">
          <div className="flex items-center gap-3 px-1">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture_path || user?.avatar} />
              <AvatarFallback className="bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white text-xs font-semibold">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">
                {toTitleCase(user?.full_name || user?.name || "User")}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {user?.role_display || user?.role || user?.email}
              </div>
            </div>
          </div>
          <Button
            variant="destructive"
            onClick={handleLogout}
            className="w-full justify-start"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
          <div className="text-xs font-bold text-muted-foreground text-center">
            CELSZC Portal
          </div>
        </div>
      </aside>

      <div
        className={cn(
          "lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm transition-opacity duration-300",
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
        aria-hidden
      />

      <aside
        className={cn(
          "lg:hidden fixed left-0 top-0 h-full w-[280px] max-w-[85vw] flex-col border-r border-border z-50 flex shadow-2xl transition-transform duration-300 ease-out bg-background pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-[#1E74FD] to-blue-600">
          <div className="flex items-center gap-2 min-w-0">
            <BrandLogo imgClassName="h-8 w-8 rounded-md bg-white/15 p-0.5" />
            <h2 className="text-lg font-semibold text-white truncate">Menu</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-blue-700 transition-colors text-white min-h-11 min-w-11 inline-flex items-center justify-center"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 p-4 overflow-y-auto overscroll-contain">
          {renderNav(onClose)}
        </nav>

        <div className="p-4 border-t border-border space-y-2">
          <Button
            variant="destructive"
            onClick={handleLogout}
            className="w-full justify-start"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
          <div className="text-xs font-bold text-muted-foreground text-center">
            CELSZC Portal
          </div>
        </div>
      </aside>
    </>
  );
}
